/**
 * Şablonu gönder (backlog #584, backend POST /email/send): kayıtlı bir SERBEST şablon, seçilen kişilere olduğu gibi gider.
 *
 * Gönderilen metin şablonun kendisidir: bu formda konu ya da metin yazılmaz (değiştirmek için önce şablon düzenlenir), böylece kayıtta tam olarak
 * şablonun dediği görünür. Kullanıcı alıcılar kendi dillerinde okur (İngilizcesi bitmişse İngilizce); sistemde kaydı olmayan adresler, burada seçilen dilde.
 * Gönderen: kendi hesaplarınız; sistemin varsayılanı ve genel hesapları yalnızca Sistem Ayarları yetkisi olanlara gelir (liste sunucudan gelir,
 * yetkisiz kimse listede görmez). En çok 25 alıcı ve saatte 100 posta; posta sıraya alınır, her birinin sonucu E-posta Kayıtları'nda görünür.
 *
 * `isOpen` kapalıyken hiçbir şey çizilmez: her açılışta form sıfırdan başlar.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Badge, Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { getSendOptions, listFreeTemplates, previewTemplate, searchSendUsers, sendTemplateMail } from "../../../../../services/emailAyarService";

const TR = 1;
const EN = 2;

/** Yazılan adres kutusu → tekil adresler (virgül, noktalı virgül, boşluk ya da satır sonu ayırır; büyük-küçük harf aynı sayılır). */
export function adresleriAyir(metin) {
    const gorulen = new Set();
    const out = [];
    (metin || "").split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean).forEach((s) => {
        const k = s.toLowerCase();
        if (!gorulen.has(k)) {
            gorulen.add(k);
            out.push(s);
        }
    });
    return out;
}

const SablonForm = ({ toggle, onSent }) => {
    const { t } = useTranslation("moduller");
    const [secenekler, setSecenekler] = useState(null);       // { senders, max_recipients, mails_per_hour, remaining }
    const [sablonlar, setSablonlar] = useState([]);
    const [yukleniyor, setYukleniyor] = useState(true);
    const [yuklemeHatasi, setYuklemeHatasi] = useState(null);
    const [sablonId, setSablonId] = useState("");
    const [dil, setDil] = useState(TR);                        // önizleme dili
    const [onizleme, setOnizleme] = useState(null);            // { anahtar, veri | hata }
    const [gonderenId, setGonderenId] = useState("");
    const [sorgu, setSorgu] = useState("");
    const [bulunan, setBulunan] = useState(null);              // null = henüz aranmadı
    const [araniyor, setAraniyor] = useState(false);
    const [secilen, setSecilen] = useState([]);                // [{ user_id, etiket }]
    const [adresMetni, setAdresMetni] = useState("");
    const [adresDili, setAdresDili] = useState(String(TR));
    const [gonderiyor, setGonderiyor] = useState(false);
    const [hata, setHata] = useState(null);
    const [sonuc, setSonuc] = useState(null);

    useEffect(() => {
        let ignore = false;
        async function run() {
            try {
                const [s, l] = await Promise.all([getSendOptions(), listFreeTemplates()]);
                if (ignore) return;
                setSecenekler(s);
                setSablonlar(l);
                const ilk = (s.senders || []).find((x) => x.kind === "OWN") || (s.senders || [])[0];
                if (ilk) setGonderenId(String(ilk.system_email_id));
            } catch (e) {
                if (!ignore) setYuklemeHatasi(e.message);
            } finally {
                if (!ignore) setYukleniyor(false);
            }
        }
        run();
        return () => { ignore = true; };
    }, []);

    const onizlemeAnahtari = sablonId ? `${sablonId}:${dil}` : null;
    useEffect(() => {
        if (!onizlemeAnahtari) return undefined;
        const [id, dilKodu] = onizlemeAnahtari.split(":");
        let ignore = false;
        async function run() {
            try {
                const veri = await previewTemplate(Number(id), Number(dilKodu));
                if (!ignore) setOnizleme({ anahtar: onizlemeAnahtari, veri });
            } catch (e) {
                if (!ignore) setOnizleme({ anahtar: onizlemeAnahtari, hata: e.message });
            }
        }
        run();
        return () => { ignore = true; };
    }, [onizlemeAnahtari]);
    const buOnizleme = onizleme && onizleme.anahtar === onizlemeAnahtari ? onizleme : null;

    const max = secenekler?.max_recipients ?? 25;
    const adresler = adresleriAyir(adresMetni);
    const toplam = secilen.length + adresler.length;
    const gonderenler = secenekler?.senders || [];
    // 2026-10-30 (Bilge): the list also holds the system mails (template_scope SYSTEM). One goes to chosen USERS only (each gets a new password link, in their
    // own language, from the default sender; sending it again is a resend), so no sender and no typed address; the backend needs the Users page's permission.
    const sistemSecili = sablonlar.some((s) => String(s.email_template_id) === String(sablonId) && s.template_scope === "SYSTEM");
    // WAS (#584): const gonderebilir = !!sablonId && !!gonderenId && toplam > 0 && toplam <= max && !gonderiyor;
    const gonderebilir = sistemSecili
        ? !!sablonId && secilen.length > 0 && adresler.length === 0 && secilen.length <= max && !gonderiyor
        : !!sablonId && !!gonderenId && toplam > 0 && toplam <= max && !gonderiyor;

    async function ara() {
        if (sorgu.trim().length < 2) return;
        setAraniyor(true);
        try {
            setBulunan(await searchSendUsers(sorgu.trim()));
            setHata(null);
        } catch (e) {
            setHata(e.message);
        } finally {
            setAraniyor(false);
        }
    }

    function ekle(u) {
        if (secilen.some((x) => x.user_id === u.user_id)) return;
        setSecilen([...secilen, { user_id: u.user_id, etiket: `${u.user_name || ""} ${u.user_surname || ""}`.trim() + (u.user_email ? ` (${u.user_email})` : "") }]);
    }

    async function gonder() {
        setGonderiyor(true);
        setHata(null);
        try {
            const cevap = await sendTemplateMail(sistemSecili
                ? { templateId: Number(sablonId), userIds: secilen.map((x) => x.user_id) }
                : {
                    templateId: Number(sablonId),
                    senderId: Number(gonderenId),
                    userIds: secilen.map((x) => x.user_id),
                    addresses: adresler,
                    languageId: Number(adresDili),
                });
            setSonuc(cevap);
            if (onSent) onSent(cevap);
        } catch (e) {
            setHata(e.message);
        } finally {
            setGonderiyor(false);
        }
    }

    const tur = (kind) => t(kind === "OWN" ? "emailsGonder.turBenim" : kind === "DEFAULT" ? "emailsGonder.turVarsayilan" : "emailsGonder.turGenel");

    return (
        <Modal isOpen toggle={toggle} size="lg" scrollable>
            <ModalHeader toggle={toggle}>{t("emailsGonder.baslik")}</ModalHeader>
            <ModalBody>
                {yukleniyor && <div className="text-center py-4"><Spinner size="sm" /></div>}
                {yuklemeHatasi && <Alert color="danger">{yuklemeHatasi}</Alert>}

                {!yukleniyor && !yuklemeHatasi && sonuc && (
                    <div>
                        <Alert color={sonuc.queued > 0 ? "success" : "warning"}>
                            {sonuc.queued > 0 ? t(sonuc.system_mail ? "emailsGonder.sonucGonderildi" : "emailsGonder.sonucSirada", { n: sonuc.queued }) : t("emailsGonder.sonucHicbiri")}
                        </Alert>
                        {sonuc.queued > 0 && <p className="text-muted">{t("emailsGonder.sonucAciklama")}</p>}
                        {sonuc.skipped?.length > 0 && (
                            <div>
                                <h6>{t("emailsGonder.sonucAtlanan")}</h6>
                                <ul className="mb-0">
                                    {sonuc.skipped.map((s, i) => (
                                        <li key={`${s.code}-${i}`}>
                                            <span className="fw-medium">{s.to || `#${s.user_id}`}</span> — {s.reason}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {!yukleniyor && !yuklemeHatasi && !sonuc && (
                    <div>
                        <p className="text-muted">{t("emailsGonder.aciklama")}</p>

                        <div className="mb-3">
                            <Label className="form-label" htmlFor="gonder-sablon">{t("emailsGonder.sablon")}</Label>
                            <Input id="gonder-sablon" type="select" value={sablonId} onChange={(e) => setSablonId(e.target.value)}>
                                <option value="">{t("emailsGonder.sablonSec")}</option>
                                {sablonlar.map((s) => (
                                    <option key={s.email_template_id} value={s.email_template_id}>
                                        {s.template_scope === "SYSTEM" ? `${s.email_template_title} · ${t("emailsGonder.sistemEtiketi")}` : s.email_template_title}
                                    </option>
                                ))}
                            </Input>
                            {sablonlar.length === 0 && <div className="form-text">{t("emailsGonder.sablonYok")}</div>}
                            {sistemSecili && <Alert color="info" className="py-2 mt-2 mb-0">{t("emailsGonder.sistemNotu")}</Alert>}
                            {sistemSecili && adresler.length > 0 && <Alert color="warning" className="py-2 mt-2 mb-0">{t("emailsGonder.sistemSadeceKullanici")}</Alert>}
                        </div>

                        {sablonId && (
                            <div className="mb-3">
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <span className="form-label mb-0">{t("emailsGonder.onizleme")}</span>
                                    <Button size="sm" color={dil === TR ? "primary" : "light"} onClick={() => setDil(TR)}>{t("emailsGonder.turkce")}</Button>
                                    <Button size="sm" color={dil === EN ? "primary" : "light"} onClick={() => setDil(EN)}>{t("emailsGonder.ingilizce")}</Button>
                                </div>
                                {buOnizleme?.hata && <Alert color="danger" className="py-2">{buOnizleme.hata}</Alert>}
                                {buOnizleme?.veri && (
                                    <div>
                                        <div className="small mb-1"><span className="text-muted">{t("emailsGonder.konu")}: </span>{buOnizleme.veri.title}</div>
                                        {dil === EN && buOnizleme.veri.language_id !== EN && (
                                            <div className="small text-warning mb-1">{t("emailsGonder.ingilizceYok")}</div>
                                        )}
                                        <iframe
                                            title={t("emailsGonder.onizlemeIframe")}
                                            sandbox=""
                                            srcDoc={buOnizleme.veri.content || ""}
                                            style={{ width: "100%", minHeight: 220, border: "1px solid var(--vz-border-color, #dee2e6)", borderRadius: 4, background: "#fff" }}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="mb-3">
                            <Label className="form-label" htmlFor="gonder-gonderen">{t("emailsGonder.gonderen")}</Label>
                            {gonderenler.length > 0 ? (
                                <Input id="gonder-gonderen" type="select" value={gonderenId} onChange={(e) => setGonderenId(e.target.value)}>
                                    {gonderenler.map((s) => (
                                        <option key={s.system_email_id} value={s.system_email_id}>
                                            {s.system_email_title} [{s.system_email_value}] · {tur(s.kind)}
                                        </option>
                                    ))}
                                </Input>
                            ) : (
                                <Alert color="warning" className="mb-0">
                                    {t("emailsGonder.gonderenYok")} <Link to="/EmailHesaplarim">{t("emailsGonder.hesaplarimLinki")}</Link>
                                </Alert>
                            )}
                        </div>

                        <div className="mb-3">
                            <Label className="form-label" htmlFor="gonder-ara">{t("emailsGonder.alicilar")}</Label>
                            <div className="d-flex gap-2">
                                <Input
                                    id="gonder-ara" type="search" value={sorgu} placeholder={t("emailsGonder.kullaniciAra")}
                                    onChange={(e) => setSorgu(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); ara(); } }}
                                />
                                <Button color="primary" outline disabled={araniyor || sorgu.trim().length < 2} onClick={ara}>
                                    {araniyor ? <Spinner size="sm" /> : t("emailsGonder.ara")}
                                </Button>
                            </div>
                            {bulunan && bulunan.length === 0 && <div className="form-text">{t("emailsGonder.sonucYokKullanici")}</div>}
                            {bulunan && bulunan.length > 0 && (
                                <ul className="list-unstyled mt-2 mb-0">
                                    {bulunan.map((u) => (
                                        <li key={u.user_id} className="d-flex align-items-center justify-content-between py-1">
                                            <span>{u.user_name} {u.user_surname} <span className="text-muted">({u.user_email || "—"})</span></span>
                                            <Button size="sm" color="light" disabled={secilen.some((x) => x.user_id === u.user_id)} onClick={() => ekle(u)}>
                                                {t("emailsGonder.ekle")}
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {secilen.length > 0 && (
                                <div className="d-flex flex-wrap gap-2 mt-2">
                                    {secilen.map((x) => (
                                        <Badge key={x.user_id} color="primary" className="d-inline-flex align-items-center gap-1">
                                            {x.etiket}
                                            <button
                                                type="button" className="btn-close btn-close-white ms-1" style={{ fontSize: "0.55rem" }}
                                                aria-label={`${t("emailsGonder.cikar")} ${x.etiket}`}
                                                onClick={() => setSecilen(secilen.filter((y) => y.user_id !== x.user_id))}
                                            />
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="mb-3">
                            <Label className="form-label" htmlFor="gonder-adresler">{t("emailsGonder.adresler")}</Label>
                            <Input id="gonder-adresler" type="textarea" rows={2} value={adresMetni} onChange={(e) => setAdresMetni(e.target.value)} />
                            <div className="form-text">{t("emailsGonder.adreslerIpucu")}</div>
                            {adresler.length > 0 && (
                                <div className="d-flex align-items-center gap-2 mt-2">
                                    <Label className="form-label mb-0" htmlFor="gonder-dil">{t("emailsGonder.adresDili")}</Label>
                                    <Input id="gonder-dil" type="select" style={{ width: "auto" }} value={adresDili} onChange={(e) => setAdresDili(e.target.value)}>
                                        <option value={String(TR)}>{t("emailsGonder.turkce")}</option>
                                        <option value={String(EN)}>{t("emailsGonder.ingilizce")}</option>
                                    </Input>
                                </div>
                            )}
                        </div>

                        <div className="small text-muted">
                            <span className={toplam > max ? "text-danger fw-medium" : ""}>{t("emailsGonder.sayac", { n: toplam, max })}</span>
                            {secenekler && <span> · {t("emailsGonder.saatKalan", { n: secenekler.remaining })}</span>}
                        </div>
                        {hata && <Alert color="danger" className="mt-3 mb-0">{hata}</Alert>}
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                {!sonuc && (
                    <Button color="primary" disabled={!gonderebilir} onClick={gonder}>
                        {gonderiyor ? <Spinner size="sm" /> : t("emailsGonder.gonder")}
                    </Button>
                )}
                <Button color="light" onClick={toggle}>{sonuc ? t("emailsGonder.kapat") : t("common:iptal")}</Button>
            </ModalFooter>
        </Modal>
    );
};

const SablonGonder = ({ isOpen, toggle, onSent }) => (isOpen ? <SablonForm toggle={toggle} onSent={onSent} /> : null);

export default SablonGonder;
